<template>
    <div id="home-frame">
        <header class="app-header">
            <div class="header-top">
                <div class="header-brand">
                    <h1>
                        <span @click="$resetCards()"><strong>my</strong>247ed.com <strong>Portal</strong></span>
                    </h1>
                    <div class="header-version">v{{version}}</div>
                </div>
                <a @click="logout" class="mobile-logout">
                    <i class="fad fa-sign-out-alt"></i>
                </a>
                <div class="header-right menu">
                <div class="pb-3">
                    <b-dropdown aria-role="list" v-if="currentUser.isSuper">
                        <template #trigger="{ active }">
                            <span class="is-clickable has-text-weight-normal">
                                <i class="fad fa-building-user mr-2 hide-mobile"></i>{{ currentUser.orgTitle }}<i class="fas ml-3 mr-5" :class="active ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                            </span>
                        </template>

                        <b-dropdown-item
                            aria-role="listitem"
                            v-for="o in organizations"
                            class="has-text-black has-text-weight-normal has-text-left"
                            @click="switchOrg(o.id);"
                        >
                            {{ o.title }}
                        </b-dropdown-item>
                    </b-dropdown>
                    <span v-else class="mr-5">
                        <i class="fad fa-building-user mr-2 hide-mobile"></i>{{ currentUser.orgTitle }}
                        <b-tooltip v-if="currentUser.contentProviderId" :label="currentUser.contentProviderTitle" position="is-left">
                            <b-tag class="ml-4"><i class="fad fa-person-chalkboard has-text-primary ml-0"></i></b-tag>
                        </b-tooltip>
                    </span>

                    <span v-if="currentUser" class="hide-mobile">{{ currentUser.firstName }} {{ currentUser.lastName }}</span>
                    <a v-if="currentUser" @click="$openCard('user-settings')" class="hide-mobile">
                        <b-tooltip label="User Settings" position="is-bottom" class="ml-3">
                            <b-icon pack="fad" icon="user-cog" />
                        </b-tooltip>
                    </a>

                    <a @click="logout" class="hide-mobile">
                        <b-tooltip label="Logout" position="is-bottom" class="ml-3">
                            <b-icon pack="fad" icon="sign-out-alt" />
                        </b-tooltip>
                    </a>
                </div>
                </div>
            </div>
        </header>

        <main v-if="currentUser">
            <div class="start fadeIn content" v-show="$TIER.cards.length === 0">
                <div class="welcome" v-if="!currentUser.isSuper">
                    <h1><strong>{{ welcome }},</strong> {{ currentUser.firstName }}</h1>
                    <h3>
                        What would you like to do?
                    </h3>
                </div>

                <div class="menu-sections">
                    <div class="columns is-variable is-6 mb-0">
                        <div class="column">
                            <!-- Favorites -->
                            <div class="menu favorites-menu" :class="{'has-items': favoriteItems.length > 0}" v-if="favoriteItems.length > 0 || editingFavorites">
                                <div class="items" v-if="favoriteItems.length > 0">
                                    <div class="menu-item" v-for="item in favoriteItems" :key="'fav-' + item.id" @click="openMenuItem(item)">
                                        <span class="remove-favorite" v-if="editingFavorites" @click.stop="toggleFavorite(item.id)">
                                            <i class="fas fa-times-circle"></i>
                                        </span>
                                        <i :class="[item.iconPack || 'fad', item.icon]"></i>
                                        <span class="menu-title">{{ item.title }}</span>
                                        <span class="menu-tag" v-if="getItemBadge(item) && !item.badgeHtml">{{ getItemBadge(item) }}</span>
                                        <span class="menu-tag" v-if="getItemBadge(item) && item.badgeHtml" v-html="getItemBadge(item)"></span>
                                    </div>
                                </div>
                                <div class="favorites-empty" v-else>
                                    <p>Click the <i class="fas fa-star"></i> on menu items below to add favorites</p>
                                </div>
                            </div>
                            <h3 class="has-text-white section-menu-header" v-if="favoriteItems.length > 0">Management</h3>
                            <div class="menu">
                                <div class="items">
                                    <div class="menu-item" @click="$openCard('users')" v-if="allowed('users-list')">
                                        <span class="favorite-toggle" v-if="editingFavorites" @click.stop="toggleFavorite('users')">
                                            <i :class="isFavorite('users') ? 'fas fa-star' : 'far fa-star'"></i>
                                        </span>
                                        <i class="fad fa-users"></i>
                                        <span class="menu-title">Users</span>
                                    </div>
                                    <div class="menu-item" @click="$openCard('support-list')" v-if="allowed('support-list')">
                                        <span class="favorite-toggle" v-if="editingFavorites" @click.stop="toggleFavorite('support')">
                                            <i :class="isFavorite('support') ? 'fas fa-star' : 'far fa-star'"></i>
                                        </span>
                                        <i class="fad fa-user-headset"></i>
                                        <span class="menu-title">Support</span>
                                        <span class="menu-tag" v-if="supportBadge > 0">{{ supportBadge }}</span>
                                    </div>
                                    <div class="menu-item" @click="$openCard('task-list')" v-if="allowed('task-list')">
                                        <span class="favorite-toggle" v-if="editingFavorites" @click.stop="toggleFavorite('tasks')">
                                            <i :class="isFavorite('tasks') ? 'fas fa-star' : 'far fa-star'"></i>
                                        </span>
                                        <i class="fad fa-tasks"></i>
                                        <span class="menu-title">Tasks</span>
                                    </div>
                                    <div class="menu-item" @click="$openCard('reviews')" v-if="allowed('review')">
                                        <span class="favorite-toggle" v-if="editingFavorites" @click.stop="toggleFavorite('reviews')">
                                            <i :class="isFavorite('reviews') ? 'fas fa-star' : 'far fa-star'"></i>
                                        </span>
                                        <i class="fad fa-file-signature"></i>
                                        <span class="menu-title">Review</span>
                                        <span class="menu-tag" v-if="reviewsBadge > 0">{{ reviewsBadge }}</span>
                                    </div>
                                    <div class="menu-item" @click="$openCard('study-plans')" v-if="allowed('study-plans')">
                                        <span class="favorite-toggle" v-if="editingFavorites" @click.stop="toggleFavorite('study-plans')">
                                            <i :class="isFavorite('study-plans') ? 'fas fa-star' : 'far fa-star'"></i>
                                        </span>
                                        <i class="fad fa-clipboard-list-check"></i>
                                        <span class="menu-title">Study Plans</span>
                                        <span class="menu-tag" v-if="studyPlansBadge > 0">{{ studyPlansBadge }}</span>
                                    </div>
                                    <div class="menu-item" @click="$openCard('milestones')" v-if="allowed('milestones')">
                                        <span class="favorite-toggle" v-if="editingFavorites" @click.stop="toggleFavorite('milestones')">
                                            <i :class="isFavorite('milestones') ? 'fas fa-star' : 'far fa-star'"></i>
                                        </span>
                                        <i class="fad fa-flag-swallowtail"></i>
                                        <span class="menu-title">Reflections</span>
                                        <span class="menu-tag" v-if="milestonesBadge > 0">{{ milestonesBadge }}</span>
                                    </div>
                                    <div class="menu-item" @click="$openCard('billing-menu')" v-if="allowed('billing-menu')">
                                        <span class="favorite-toggle" v-if="editingFavorites" @click.stop="toggleFavorite('billing')">
                                            <i :class="isFavorite('billing') ? 'fas fa-star' : 'far fa-star'"></i>
                                        </span>
                                        <i class="fad fa-money-check"></i>
                                        <span class="menu-title">Billing</span>
                                    </div>
                                    <div class="menu-item" @click="$openCard('new-reseller-quote')" v-if="allowed('reseller-quotes')">
                                        <span class="favorite-toggle" v-if="editingFavorites" @click.stop="toggleFavorite('new-reseller-quote')">
                                            <i :class="isFavorite('new-reseller-quote') ? 'fas fa-star' : 'far fa-star'"></i>
                                        </span>
                                        <i class="fad fa-file-circle-plus"></i>
                                        <span class="menu-title">New Quote</span>
                                    </div>
                                    <div class="menu-item" @click="$openCard('reseller-enroll-report', {userId: 0})" v-if="allowed('reseller-enroll-report')">
                                        <span class="favorite-toggle" v-if="editingFavorites" @click.stop="toggleFavorite('reseller-enroll')">
                                            <i :class="isFavorite('reseller-enroll') ? 'fas fa-star' : 'far fa-star'"></i>
                                        </span>
                                        <i class="fad fa-clipboard-check"></i>
                                        <span class="menu-title">Enrollments</span>
                                    </div>
                                    <div class="menu-item" @click="$openCard('reseller-client-database')" v-if="allowed('reseller-client-database')">
                                        <span class="favorite-toggle" v-if="editingFavorites" @click.stop="toggleFavorite('reseller-clients')">
                                            <i :class="isFavorite('reseller-clients') ? 'fas fa-star' : 'far fa-star'"></i>
                                        </span>
                                        <i class="fad fa-users"></i>
                                        <span class="menu-title">Clients</span>
                                    </div>
                                    <div class="menu-item" @click="$openCard('reseller-pending-plans')" v-if="allowed('reseller-pending-plans')">
                                        <span class="favorite-toggle" v-if="editingFavorites" @click.stop="toggleFavorite('reseller-pending')">
                                            <i :class="isFavorite('reseller-pending') ? 'fas fa-star' : 'far fa-star'"></i>
                                        </span>
                                        <i class="fad fa-timer"></i>
                                        <span class="menu-title">Pending Plans</span>
                                    </div>
                                    <div class="menu-item" @click="$openCard('approval')" v-if="currentUser.isApprovalCommittee && allowed('approval')">
                                        <span class="favorite-toggle" v-if="editingFavorites" @click.stop="toggleFavorite('approval')">
                                            <i :class="isFavorite('approval') ? 'fas fa-star' : 'far fa-star'"></i>
                                        </span>
                                        <i class="fad fa-memo-circle-check"></i>
                                        <span class="menu-title">Approvals</span>
                                    </div>
                                    <div class="menu-item" @click="$openCard('activity')" v-if="allowed('information')">
                                        <span class="favorite-toggle" v-if="editingFavorites" @click.stop="toggleFavorite('activity')">
                                            <i :class="isFavorite('activity') ? 'fas fa-star' : 'far fa-star'"></i>
                                        </span>
                                        <i class="fad fa-users-viewfinder"></i>
                                        <span class="menu-title">Activity</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="column is-4" v-if="currentUser.isSuper">
                            <div class="columns is-variable is-2 mb-4" v-if="allowed('users-list')">
                                <div class="column is-8">
                                    <b-input icon="user-magnifying-glass" v-model="userSearch" @keyup.enter="searchUser" @keyup.native.enter="searchUser" placeholder="Search User..."></b-input>
                                </div>
                                <div class="column is-4" v-if="allowed('reseller-quotes')">
                                    <button class="button is-small is-fullwidth new-quote-btn" @click="$openCard('new-reseller-quote')">
                                        <i class="far fa-plus mr-2"></i>New Quote
                                    </button>
                                </div>
                            </div>
                            <div class="admin-widgets">
                            <div class="stat-widget recent-users-widget" v-if="recentUsers.length">
                                <div class="widget-header">
                                    <i class="fad fa-clock-rotate-left"></i>
                                    <span class="widget-title">Recent Users</span>
                                </div>
                                <div class="widget-body">
                                    <template v-for="(entry, index) in recentUsers">
                                        <div class="recent-user-row" :key="entry.userId + '-' + entry.openedById" @click="$openCard('user', {userId: entry.userId})">
                                            <span class="recent-user-name"><i class="far fa-user fa-fw mr-2"></i>{{ entry.firstName }} {{ entry.lastName }} ({{ entry.userId }})</span>
                                        </div>
                                        <hr class="recent-user-divider" v-if="index < recentUsers.length - 1" :key="'d-' + entry.userId + '-' + entry.openedById">
                                    </template>
                                </div>
                            </div>
                            <div class="stat-widget">
                                <div class="widget-header">
                                    <i class="fad fa-users"></i>
                                    <span class="widget-title">Users</span>
                                </div>
                                <div class="widget-body">
                                    <div class="stat-row">
                                        <span class="stat-label">Signups Today</span>
                                        <span class="stat-value">
                                            {{ dashboardStats.users_signups_today || 0 }}
                                            <span class="trend" :class="trendClass('users_signups')" v-if="showTrend('users_signups')">
                                                <i class="fas" :class="trendIcon('users_signups')"></i>
                                                {{ trendDiff('users_signups') }}
                                            </span>
                                        </span>
                                    </div>
                                    <div class="stat-row">
                                        <span class="stat-label">Logins Today</span>
                                        <span class="stat-value">
                                            {{ dashboardStats.users_logins_today || 0 }}
                                            <span class="trend" :class="trendClass('users_logins')" v-if="showTrend('users_logins')">
                                                <i class="fas" :class="trendIcon('users_logins')"></i>
                                                {{ trendDiff('users_logins') }}
                                            </span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                            </div>
                        </div>
                    </div>

                    <!-- Content Section -->
                    <div class="section-menu" v-if="allowed('course-menu-list')">
                        <h3 class="has-text-white">Content</h3>
                        <div class="columns is-variable is-6 mb-0">
                            <div class="column">
                                <div class="menu">
                                    <div class="items items-compact">
                                        <div class="menu-item menu-item-sm" @click="$openCard('courses')" v-if="allowed('course-menu-list')">
                                            <span class="favorite-toggle" v-if="editingFavorites" @click.stop="toggleFavorite('courses')">
                                                <i :class="isFavorite('courses') ? 'fas fa-star' : 'far fa-star'"></i>
                                            </span>
                                            <i class="fad fa-chalkboard-teacher"></i>
                                            <span class="menu-title">Courses</span>
                                        </div>
                                        <div class="menu-item menu-item-sm" @click="$openCard('conferences')" v-if="allowed('conferences')">
                                            <span class="favorite-toggle" v-if="editingFavorites" @click.stop="toggleFavorite('conferences')">
                                                <i :class="isFavorite('conferences') ? 'fas fa-star' : 'far fa-star'"></i>
                                            </span>
                                            <i class="fad fa-users-class"></i>
                                            <span class="menu-title">Conferences</span>
                                        </div>
                                        <div class="menu-item menu-item-sm" @click="$openCard('applications')" v-if="allowed('course-menu-list')">
                                            <span class="favorite-toggle" v-if="editingFavorites" @click.stop="toggleFavorite('applications')">
                                                <i :class="isFavorite('applications') ? 'fas fa-star' : 'far fa-star'"></i>
                                            </span>
                                            <i class="fad fa-file-certificate"></i>
                                            <span class="menu-title">Applications</span>
                                        </div>
                                        <div class="menu-item menu-item-sm" @click="$openCard('certifications')" v-if="(currentUser.isAdmin || currentUser.isSuper) && allowed('course-menu-list')">
                                            <span class="favorite-toggle" v-if="editingFavorites" @click.stop="toggleFavorite('certifications')">
                                                <i :class="isFavorite('certifications') ? 'fas fa-star' : 'far fa-star'"></i>
                                            </span>
                                            <i class="far fa-certificate"></i>
                                            <span class="menu-title">Certifications</span>
                                        </div>
                                        <div class="menu-item menu-item-sm" @click="$openCard('media-library')" v-if="allowed('course-menu-list')">
                                            <span class="favorite-toggle" v-if="editingFavorites" @click.stop="toggleFavorite('media-library')">
                                                <i :class="isFavorite('media-library') ? 'fas fa-star' : 'far fa-star'"></i>
                                            </span>
                                            <i class="fad fa-photo-film-music"></i>
                                            <span class="menu-title">Media Library</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="column is-4 admin-widgets" v-if="currentUser.isSuper">
                                <div class="stat-widget">
                                    <div class="widget-header">
                                        <i class="fad fa-chalkboard-teacher"></i>
                                        <span class="widget-title">Courses</span>
                                    </div>
                                    <div class="widget-body">
                                        <div class="stat-row">
                                            <span class="stat-label">Enrollments Today</span>
                                            <span class="stat-value">
                                                {{ dashboardStats.courses_enrollments_today || 0 }}
                                                <span class="trend" :class="trendClass('courses_enrollments')" v-if="showTrend('courses_enrollments')">
                                                    <i class="fas" :class="trendIcon('courses_enrollments')"></i>
                                                    {{ trendDiff('courses_enrollments') }}
                                                </span>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div class="stat-widget">
                                    <div class="widget-header">
                                        <i class="fad fa-users-class"></i>
                                        <span class="widget-title">Conferences</span>
                                    </div>
                                    <div class="widget-body">
                                        <div class="stat-row">
                                            <span class="stat-label">Registrations Today</span>
                                            <span class="stat-value">
                                                {{ dashboardStats.conferences_registrations_today || 0 }}
                                                <span class="trend" :class="trendClass('conferences_registrations')" v-if="showTrend('conferences_registrations')">
                                                    <i class="fas" :class="trendIcon('conferences_registrations')"></i>
                                                    {{ trendDiff('conferences_registrations') }}
                                                </span>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Finance Section -->
                    <div class="section-menu" v-if="allowed('payment-plans-menu') || currentUser.isSuper">
                        <h3 class="has-text-white">Finance</h3>
                        <div class="columns is-variable is-6 mb-0">
                            <div class="column">
                                <div class="menu">
                                    <div class="items items-compact">
                                        <div class="menu-item menu-item-sm" @click="$openCard('reseller-quotes')" v-if="allowed('reseller-quotes')">
                                            <span class="favorite-toggle" v-if="editingFavorites" @click.stop="toggleFavorite('reseller-quotes')">
                                                <i :class="isFavorite('reseller-quotes') ? 'fas fa-star' : 'far fa-star'"></i>
                                            </span>
                                            <i class="fad fa-file-invoice-dollar"></i>
                                            <span class="menu-title">Quotes</span>
                                        </div>
                                        <div class="menu-item menu-item-sm" @click="$openCard('payment-plans')" v-if="allowed('payment-plans-menu')">
                                            <span class="favorite-toggle" v-if="editingFavorites" @click.stop="toggleFavorite('payment-plans')">
                                                <i :class="isFavorite('payment-plans') ? 'fas fa-star' : 'far fa-star'"></i>
                                            </span>
                                            <i class="fad fa-money-check-alt"></i>
                                            <span class="menu-title">Payment Plans</span>
                                            <span class="menu-tag" v-if="plansBadge" v-html="plansBadge"></span>
                                        </div>
                                        <div class="menu-item menu-item-sm" @click="$openCard('transactions-menu')" v-if="currentUser.isSuper">
                                            <span class="favorite-toggle" v-if="editingFavorites" @click.stop="toggleFavorite('transactions')">
                                                <i :class="isFavorite('transactions') ? 'fas fa-star' : 'far fa-star'"></i>
                                            </span>
                                            <i class="fad fa-usd-circle"></i>
                                            <span class="menu-title">Transactions</span>
                                        </div>
                                        <div class="menu-item menu-item-sm" @click="$openCard('sales-tax-menu')" v-if="allowed('sales-tax-menu')">
                                            <span class="favorite-toggle" v-if="editingFavorites" @click.stop="toggleFavorite('sales-tax')">
                                                <i :class="isFavorite('sales-tax') ? 'fas fa-star' : 'far fa-star'"></i>
                                            </span>
                                            <i class="fad fa-university"></i>
                                            <span class="menu-title">Sales Tax</span>
                                        </div>
                                        <div class="menu-item menu-item-sm" @click="$openCard('plans-list', {type: 'collections'})" v-if="allowed('collections')">
                                            <span class="favorite-toggle" v-if="editingFavorites" @click.stop="toggleFavorite('collections')">
                                                <i :class="isFavorite('collections') ? 'fas fa-star' : 'far fa-star'"></i>
                                            </span>
                                            <i class="fad fa-gavel"></i>
                                            <span class="menu-title">Collections</span>
                                        </div>
                                        <div class="menu-item menu-item-sm" @click="$openCard('resellers')" v-if="allowed('resellers')">
                                            <span class="favorite-toggle" v-if="editingFavorites" @click.stop="toggleFavorite('resellers')">
                                                <i :class="isFavorite('resellers') ? 'fas fa-star' : 'far fa-star'"></i>
                                            </span>
                                            <i class="fad fa-user-tie"></i>
                                            <span class="menu-title">Resellers</span>
                                        </div>
                                        <div class="menu-item menu-item-sm" @click="$openCard('quotes')" v-if="allowed('quotes')">
                                            <span class="favorite-toggle" v-if="editingFavorites" @click.stop="toggleFavorite('quotes')">
                                                <i :class="isFavorite('quotes') ? 'fas fa-star' : 'far fa-star'"></i>
                                            </span>
                                            <i class="fad fa-file-signature"></i>
                                            <span class="menu-title">Reseller Quotes</span>
                                        </div>
                                        <div class="menu-item menu-item-sm" @click="$openCard('forecast-report')" v-if="allowed('reports')">
                                            <span class="favorite-toggle" v-if="editingFavorites" @click.stop="toggleFavorite('forecast')">
                                                <i :class="isFavorite('forecast') ? 'fas fa-star' : 'far fa-star'"></i>
                                            </span>
                                            <i class="fad fa-chart-line"></i>
                                            <span class="menu-title">Forecast</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="column is-4 admin-widgets" v-if="currentUser.isSuper">
                                <div class="stat-widget">
                                    <div class="widget-header">
                                        <i class="fad fa-money-check-alt"></i>
                                        <span class="widget-title">Payment Plans</span>
                                    </div>
                                    <div class="widget-body">
                                        <div class="stat-row">
                                            <span class="stat-label">Created This Month</span>
                                            <span class="stat-value">
                                                {{ dashboardStats.payment_plans_this_month || 0 }}
                                                <span class="trend" :class="trendClassMonth('payment_plans')" v-if="showTrendMonth('payment_plans')">
                                                    <i class="fas" :class="trendIconMonth('payment_plans')"></i>
                                                    {{ trendDiffMonth('payment_plans') }}
                                                </span>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- Tools & Settings Section -->
                    <div class="section-menu" v-if="allowed('tools') || allowed('org-settings') || allowed('wiaa-certification-lookup')">
                        <h3 class="has-text-white">Tools &amp; Settings</h3>
                        <div class="columns is-variable is-6 mb-0">
                            <div class="column">
                                <div class="menu">
                                    <div class="items items-compact">
                                        <div class="menu-item menu-item-sm" @click="$openCard('org-settings')" v-if="(currentUser.isAdmin || currentUser.isSuper) && allowed('org-settings')">
                                            <span class="favorite-toggle" v-if="editingFavorites" @click.stop="toggleFavorite('org-settings')">
                                                <i :class="isFavorite('org-settings') ? 'fas fa-star' : 'far fa-star'"></i>
                                            </span>
                                            <i class="fad fa-building-user"></i>
                                            <span class="menu-title">Organization</span>
                                        </div>
                                        <div class="menu-item menu-item-sm" @click="$openCard('content-providers')" v-if="(currentUser.isAdmin || currentUser.isSuper) && allowed('content-providers')">
                                            <span class="favorite-toggle" v-if="editingFavorites" @click.stop="toggleFavorite('content-providers')">
                                                <i :class="isFavorite('content-providers') ? 'fas fa-star' : 'far fa-star'"></i>
                                            </span>
                                            <i class="fad fa-person-chalkboard"></i>
                                            <span class="menu-title">Providers</span>
                                        </div>
                                        <div class="menu-item menu-item-sm" @click="$openCard('tools')" v-if="allowed('tools')">
                                            <span class="favorite-toggle" v-if="editingFavorites" @click.stop="toggleFavorite('tools')">
                                                <i :class="isFavorite('tools') ? 'fas fa-star' : 'far fa-star'"></i>
                                            </span>
                                            <i class="fad fa-tools"></i>
                                            <span class="menu-title">Tools</span>
                                        </div>
                                        <div class="menu-item menu-item-sm" @click="$openCard('wiaa-certification-lookup')" v-if="allowed('wiaa-certification-lookup')">
                                            <span class="favorite-toggle" v-if="editingFavorites" @click.stop="toggleFavorite('wiaa-lookup')">
                                                <i :class="isFavorite('wiaa-lookup') ? 'fas fa-star' : 'far fa-star'"></i>
                                            </span>
                                            <i class="fad fa-search-plus"></i>
                                            <span class="menu-title">WIAA Lookup</span>
                                        </div>
                                        <div class="menu-item menu-item-sm" @click="$openCard('user-settings')" v-if="allowed('user-settings')">
                                            <span class="favorite-toggle" v-if="editingFavorites" @click.stop="toggleFavorite('user-settings')">
                                                <i :class="isFavorite('user-settings') ? 'fas fa-star' : 'far fa-star'"></i>
                                            </span>
                                            <i class="fad fa-user-cog"></i>
                                            <span class="menu-title">User Settings</span>
                                        </div>
                                        <div class="menu-item menu-item-sm" @click="logout" v-if="allowed('logout')">
                                            <i class="fad fa-sign-out"></i>
                                            <span class="menu-title">Logout</span>
                                        </div>
                                    </div>
                                </div>
                                <div class="sub-menu" v-if="currentUser.isSuper">
                                    <div class="menu-item" @click="openUrl('https://app.thepaymentoption.com/api/scripts/stats')" v-if="allowed('tpo')">
                                        <i class="fad fa-money-bill-trend-up"></i>
                                        <span>TPO Stats</span>
                                    </div>
                                    <div class="menu-item" @click="openUrl('https://dashboard.stripe.com/')">
                                        <i class="fad fa-credit-card"></i>
                                        <span>Stripe</span>
                                    </div>
                                    <div class="menu-item" @click="openUrl('https://app.staxpayments.com/')">
                                        <i class="fad fa-money-check"></i>
                                        <span>Stax</span>
                                    </div>
                                    <div class="menu-item" @click="openUrl('https://www.convergepay.com/converge-webapp/#!/login')">
                                        <i class="fad fa-square-dollar"></i>
                                        <span>Converge</span>
                                    </div>
                                    <div class="menu-item" @click="openUrl('https://apps.docusign.com/send/home')">
                                        <i class="fad fa-signature"></i>
                                        <span>Docusign</span>
                                    </div>
                                    <div class="menu-item" @click="openUrl('https://app.mailgun.com/')">
                                        <i class="fad fa-mail-bulk"></i>
                                        <span>Mailgun</span>
                                    </div>
                                    <div class="menu-item" @click="openUrl('https://dashboard.clicksend.com/home')">
                                        <i class="fad fa-message-sms"></i>
                                        <span>Clicksend</span>
                                    </div>
                                    <div class="menu-item" @click="openUrl('https://dashboard.postgrid.com/')">
                                        <i class="fad fa-envelope"></i>
                                        <span>PostGrid</span>
                                    </div>
                                    <div class="menu-item" @click="openUrl('https://analytics.google.com/analytics/web/#/')">
                                        <i class="fad fa-earth-americas"></i>
                                        <span>Analytics</span>
                                    </div>
                                    <div class="menu-item" @click="openUrl('https://logs.betterstack.com')">
                                        <i class="fad fa-square-terminal"></i>
                                        <span>Better Stack</span>
                                    </div>
                                    <div class="menu-item" @click="openUrl('https://deverlopers.google.com')">
                                        <i class="fa-brands fa-google"></i>
                                        <span>Google Dev</span>
                                    </div>
                                    <div class="menu-item" @click="$openCard('rmc-conferences')" v-if="allowed('rmc-dashboard')">
                                        <i class="fad fa-users-class"></i>
                                        <span>RMC</span>
                                    </div>
                                </div>
                            </div>
                            <div class="column is-4" v-if="currentUser.isSuper">
                            </div>
                        </div>
                    </div>
                </div>

                <div class="favorites-bar">
                    <a v-if="favoriteItems.length === 0 && !editingFavorites" @click="editingFavorites = true" class="add-favorites-link">
                        <i class="far fa-star"></i> Add Favorites
                    </a>
                    <a v-if="favoriteItems.length > 0 || editingFavorites" @click="editingFavorites = !editingFavorites" class="edit-link">
                        <i class="far fa-star"></i> {{ editingFavorites ? 'Done Editing' : 'Edit Favorites' }}
                    </a>
                    <span class="version-label"><strong>Portal</strong> v{{version}}</span>
                </div>
            </div>

            <stack class="cards" v-show="$TIER.cards.length" />
        </main>

        <footer class="columns is-gapless" v-if="currentUser">
            <div class="column is-10">
                <ul class="mh-breadcrumb" :class="{'active': $TIER.cards.length}">
                    <li @click="$resetCards()"><span><i class="fad fa-home" style="margin-right: 0"></i></span></li>
                    <li v-for="(card, index) in $TIER.cards" :class="{'active': index >= $TIER.activeCard && index < $TIER.activeCard + $TIER.cardCount}" @click="$setActiveCard(index)">
                        <span><i class="fad" :class="card.definition.icon" v-if="card.definition.icon"></i>{{ card.title || card.definition.title || card.definition.component }}</span>
                    </li>
                </ul>
            </div>
            <div class="column has-text-right">
                <span v-if="$TIER.saveAlert.active" class="has-text-warning is-size-7 has-text-weight-bold"><i class="far fa-exclamation-triangle mr-2"></i>Unsaved Changes</span>
            </div>
        </footer>

        <div class="ml-5 pl-5 has-text-white" v-else>Invalid request. Please logout.</div>
    </div>
</template>

<script>
    import {mapGetters, mapActions} from 'vuex';
    import {allowed} from "@/constants/permissions";
    import Stack from "../TIER/components/Stack";
    import {client as http} from '@/http_client';
    import async from "async";
    import pkg from '../../package.json';
    import {trackViewer, removeViewer} from '@/components/supportViewingTracker';
    import {trackUserOpened, getRecentUsers} from '@/components/recentUserTracker';

    export default {
        name: 'secure',
        components: {Stack},
        data: function () {
            return {
                organizations: [],
                userSearch: '',
                statusPollTimer: null,
                version: pkg.version,
                favorites: [],
                editingFavorites: false,
                supportStats: null,
                menuItems: [
                    { id: 'users', card: 'users', permission: 'users-list', icon: 'fa-users', title: 'Users' },
                    { id: 'support', card: 'support-list', permission: 'support-list', icon: 'fa-user-headset', title: 'Support', badge: 'supportBadge' },
                    { id: 'tasks', card: 'task-list', permission: 'task-list', icon: 'fa-tasks', title: 'Tasks' },
                    { id: 'reviews', card: 'reviews', permission: 'review', icon: 'fa-file-signature', title: 'Review', badge: 'reviewsBadge' },
                    { id: 'billing', card: 'billing-menu', permission: 'billing-menu', icon: 'fa-money-check', title: 'Billing' },
                    { id: 'payment-plans', card: 'payment-plans', permission: 'payment-plans-menu', icon: 'fa-money-check-alt', title: 'Payment Plans', badge: 'plansBadge', badgeHtml: true },
                    { id: 'transactions', card: 'transactions-menu', superOnly: true, icon: 'fa-usd-circle', title: 'Transactions' },
                    { id: 'sales-tax', card: 'sales-tax-menu', permission: 'sales-tax-menu', icon: 'fa-university', title: 'Sales Tax' },
                    { id: 'collections', card: 'plans-list', cardParams: {type: 'collections'}, permission: 'collections', icon: 'fa-gavel', title: 'Collections' },
                    { id: 'quotes', card: 'quotes', permission: 'quotes', icon: 'fa-file-signature', title: 'Reseller Quotes' },
                    { id: 'reseller-enroll', card: 'reseller-enroll-report', cardParams: {userId: 0}, permission: 'reseller-enroll-report', icon: 'fa-clipboard-check', title: 'Enrollments' },
                    { id: 'reseller-clients', card: 'reseller-client-database', permission: 'reseller-client-database', icon: 'fa-users', title: 'Clients' },
                    { id: 'reseller-quotes', card: 'reseller-quotes', permission: 'reseller-quotes', icon: 'fa-file-invoice-dollar', title: 'Quotes' },
                    { id: 'reseller-pending', card: 'reseller-pending-plans', permission: 'reseller-pending-plans', icon: 'fa-timer', title: 'Pending Plans' },
                    { id: 'new-reseller-quote', card: 'new-reseller-quote', permission: 'reseller-quotes', icon: 'fa-file-circle-plus', title: 'New Quote' },
                    { id: 'resellers', card: 'resellers', permission: 'resellers', icon: 'fa-user-tie', title: 'Resellers' },
                    { id: 'courses', card: 'courses', permission: 'course-menu-list', icon: 'fa-chalkboard-teacher', title: 'Courses' },
                    { id: 'conferences', card: 'conferences', permission: 'conferences', icon: 'fa-users-class', title: 'Conferences' },
                    { id: 'applications', card: 'applications', permission: 'course-menu-list', icon: 'fa-file-certificate', title: 'Applications' },
                    { id: 'certifications', card: 'certifications', permission: 'course-menu-list', adminOnly: true, icon: 'fa-certificate', iconPack: 'far', title: 'Certifications' },
                    { id: 'wiaa-lookup', card: 'wiaa-certification-lookup', permission: 'wiaa-certification-lookup', icon: 'fa-search-plus', title: 'WIAA Lookup' },
                    { id: 'media-library', card: 'media-library', permission: 'course-menu-list', icon: 'fa-photo-film-music', title: 'Media Library' },
                    { id: 'approval', card: 'approval', permission: 'approval', approvalCommittee: true, icon: 'fa-memo-circle-check', title: 'Approvals' },
                    { id: 'study-plans', card: 'study-plans', permission: 'study-plans', icon: 'fa-clipboard-list-check', title: 'Study Plans', badge: 'studyPlansBadge' },
                    { id: 'milestones', card: 'milestones', permission: 'milestones', icon: 'fa-flag-swallowtail', title: 'Reflections', badge: 'milestonesBadge' },
                    { id: 'activity', card: 'activity', permission: 'information', icon: 'fa-users-viewfinder', title: 'Activity' },
                    { id: 'tools', card: 'tools', permission: 'tools', icon: 'fa-tools', title: 'Tools' },
                    { id: 'forecast', card: 'forecast-report', permission: 'reports', icon: 'fa-chart-line', title: 'Forecast' },
                    { id: 'org-settings', card: 'org-settings', permission: 'org-settings', adminOnly: true, icon: 'fa-building-user', title: 'Organization' },
                    { id: 'content-providers', card: 'content-providers', permission: 'content-providers', adminOnly: true, icon: 'fa-person-chalkboard', title: 'Providers' },
                    { id: 'user-settings', card: 'user-settings', permission: 'user-settings', icon: 'fa-user-cog', title: 'User Settings' },
                    { id: 'logout', action: 'logout', permission: 'logout', icon: 'fa-sign-out', title: 'Logout' }
                ]
            };
        },
        computed: {
            ...mapGetters(['currentUser', 'reviews', 'paymentPlanCounts', 'dashboardStats', 'studyPlans', 'milestones']),
            welcome: function () {
                const currentHour = new Date().getHours();
                return currentHour >= 4 && currentHour < 12 ? 'Good morning' :
                    currentHour >= 12 && currentHour <= 17 ? 'Good afternoon' :
                        currentHour > 17 || currentHour < 4 ? 'Good evening' : 'Welcome';
            },
            plansBadge: function () {
                let badge = null;
                if (this.paymentPlanCounts.pendingPlans > 0) {
                    badge = '<i class="fad fa-timer"></i>' + this.paymentPlanCounts.pendingPlans;
                }
                if (this.paymentPlanCounts.pendingSubmit > 0) {
                    badge = (badge)
                        ? badge + '<span class="ml-2 has-text-danger"><i class="fas fa-credit-card"></i>' + this.paymentPlanCounts.pendingSubmit + '</span>'
                        : '<span class="has-text-danger"><i class="fas fa-credit-card"></i>' + this.paymentPlanCounts.pendingSubmit + '</span>';
                }
                return badge;
            },
            reviewsBadge: function () {
                let count = 0;
                if (this.reviews.enroll_applications) count += this.reviews.enroll_applications.length;
                if (this.reviews.reg_applications) count += this.reviews.reg_applications.length;
                if (this.reviews.conf_applications) count += this.reviews.conf_applications.filter(app => app.updated_at).length;
                if (this.reviews.course_applications) count += this.reviews.course_applications.filter(app => app.updated_at).length;
                return count;
            },
            studyPlansBadge: function () {
                return this.studyPlans?.pending_count || 0;
            },
            milestonesBadge: function () {
                return this.milestones?.pending_count || 0;
            },
            supportBadge: function () {
                if (!this.supportStats) return 0;
                return (this.supportStats.open_count || 0) + (this.supportStats.assigned_count || 0);
            },
            recentUsers: function () {
                return getRecentUsers();
            },
            favoriteItems: function () {
                return this.favorites
                    .map(id => this.menuItems.find(item => item.id === id))
                    .filter(item => item && this.isMenuItemVisible(item));
            }
        },
        methods: {
            trendDiff: function (metric) {
                const today = this.dashboardStats[`${metric}_today`] || 0;
                const yesterday = this.dashboardStats[`${metric}_yesterday`] || 0;
                const diff = today - yesterday;
                return Math.abs(diff);
            },
            trendIcon: function (metric) {
                const today = this.dashboardStats[`${metric}_today`] || 0;
                const yesterday = this.dashboardStats[`${metric}_yesterday`] || 0;
                if (today > yesterday) return 'fa-arrow-up';
                if (today < yesterday) return 'fa-arrow-down';
                return 'fa-minus';
            },
            trendClass: function (metric) {
                const today = this.dashboardStats[`${metric}_today`] || 0;
                const yesterday = this.dashboardStats[`${metric}_yesterday`] || 0;
                if (today > yesterday) return 'trend-up';
                if (today < yesterday) return 'trend-down';
                return 'trend-neutral';
            },
            showTrend: function (metric) {
                const today = this.dashboardStats[`${metric}_today`];
                const yesterday = this.dashboardStats[`${metric}_yesterday`];
                return today !== undefined && yesterday !== undefined;
            },
            // Monthly trend methods
            trendDiffMonth: function (metric) {
                const thisMonth = this.dashboardStats[`${metric}_this_month`] || 0;
                const lastMonth = this.dashboardStats[`${metric}_last_month`] || 0;
                const diff = thisMonth - lastMonth;
                return Math.abs(diff);
            },
            trendIconMonth: function (metric) {
                const thisMonth = this.dashboardStats[`${metric}_this_month`] || 0;
                const lastMonth = this.dashboardStats[`${metric}_last_month`] || 0;
                if (thisMonth > lastMonth) return 'fa-arrow-up';
                if (thisMonth < lastMonth) return 'fa-arrow-down';
                return 'fa-minus';
            },
            trendClassMonth: function (metric) {
                const thisMonth = this.dashboardStats[`${metric}_this_month`] || 0;
                const lastMonth = this.dashboardStats[`${metric}_last_month`] || 0;
                if (thisMonth > lastMonth) return 'trend-up';
                if (thisMonth < lastMonth) return 'trend-down';
                return 'trend-neutral';
            },
            showTrendMonth: function (metric) {
                const thisMonth = this.dashboardStats[`${metric}_this_month`];
                const lastMonth = this.dashboardStats[`${metric}_last_month`];
                return thisMonth !== undefined && lastMonth !== undefined;
            },
            ...mapActions(['signOut', 'switchOrganization', 'getReviews', 'getPaymentPlanCounts', 'getDashboardStats', 'getStudyPlansCount', 'getMilestonesCount']),
            loadOrganizations: function () {
                http.get('organizations').then(response => {
                    this.organizations = response.data;
                });
            },
            allowed: function (permission) {
                if (this.currentUser) return allowed(this.currentUser.role, permission);
                return false;
            },
            openUrl(url) {
                window.open(url);
            },
            logout: function () {
                this.signOut();
                this.$router.push({name: 'login'});
            },
            searchUser: function () {
                console.log('searchUser', this.userSearch);
                this.$openCard('user-search', {query: this.userSearch}, this.card);
            },
            loadSupportStats: function () {
                if (this.allowed('support-list')) {
                    http.get('support/stats').then(response => {
                        this.supportStats = response.data;
                    }).catch(() => {});
                }
            },
            playNotificationSound: function () {
                try {
                    let ctx = new (window.AudioContext || window.webkitAudioContext)();
                    let osc = ctx.createOscillator();
                    let gain = ctx.createGain();
                    osc.connect(gain);
                    gain.connect(ctx.destination);
                    osc.type = 'sine';
                    osc.frequency.setValueAtTime(880, ctx.currentTime);
                    osc.frequency.setValueAtTime(1046, ctx.currentTime + 0.1);
                    gain.gain.setValueAtTime(0.3, ctx.currentTime);
                    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
                    osc.start(ctx.currentTime);
                    osc.stop(ctx.currentTime + 0.3);
                } catch (e) {}
            },
            startTimer: function () {
                this.statusPollTimer = setInterval(() => {
                    this.getReviews();
                    this.getPaymentPlanCounts();
                    this.getDashboardStats();
                    this.getStudyPlansCount();
                    this.getMilestonesCount();
                    this.loadSupportStats();
                }, 300000); // Poll every 5 minutes
            },
            switchOrg: function (id) {
                this.$resetCards();
                this.switchOrganization(id).then(() => {
                    this.getReviews();
                });
            },
            isMenuItemVisible: function (item) {
                if (!this.currentUser) return false;
                if (item.superOnly && !this.currentUser.isSuper) return false;
                if (item.adminOnly && !(this.currentUser.isAdmin || this.currentUser.isSuper)) return false;
                if (item.approvalCommittee && !this.currentUser.isApprovalCommittee) return false;
                if (item.permission && !this.allowed(item.permission)) return false;
                return true;
            },
            openMenuItem: function (item) {
                if (item.action === 'logout') {
                    this.logout();
                } else {
                    this.$openCard(item.card, item.cardParams || {});
                }
            },
            getItemBadge: function (item) {
                if (!item.badge) return null;
                return this[item.badge];
            },
            isFavorite: function (itemId) {
                return this.favorites.includes(itemId);
            },
            toggleFavorite: function (itemId) {
                const index = this.favorites.indexOf(itemId);
                if (index === -1) {
                    this.favorites.push(itemId);
                } else {
                    this.favorites.splice(index, 1);
                }
                this.saveFavorites();
            },
            loadFavorites: function () {
                try {
                    const stored = localStorage.getItem('my247ed_favorites');
                    if (stored) {
                        this.favorites = JSON.parse(stored);
                    }
                } catch (e) {
                    console.error('Error loading favorites:', e);
                    this.favorites = [];
                }
            },
            saveFavorites: function () {
                try {
                    localStorage.setItem('my247ed_favorites', JSON.stringify(this.favorites));
                } catch (e) {
                    console.error('Error saving favorites:', e);
                }
            }
        },
        mounted() {
            if (!this.currentUser) this.logout();
            if (this.currentUser.isSuper) this.loadOrganizations();

            this.loadFavorites();
            this.getReviews();
            this.getPaymentPlanCounts();
            this.getDashboardStats();
            this.getStudyPlansCount();
            this.getMilestonesCount();
            this.loadSupportStats();
            this.startTimer();
        },
        sockets: {
            onmessage(event) {
                try {
                    let data = (typeof event.data === 'string') ? JSON.parse(event.data) : event.data;
                    if (data.type === 'support_viewing') {
                        if (data.action === 'viewing') {
                            trackViewer(data.ticketId, data.userId, data.userName);
                        } else if (data.action === 'stop') {
                            removeViewer(data.ticketId, data.userId);
                        }
                    }
                    if (data.type === 'user_opened') {
                        trackUserOpened(data);
                    }
                    if (data.type === 'support') {
                        this.loadSupportStats();
                        if (data.action === 'new_ticket' || data.action === 'new_message') {
                            this.playNotificationSound();
                        }
                    }
                } catch (e) {}
            }
        },
        destroyed() {
            clearInterval(this.statusPollTimer);
        }
    };
</script>

<style lang="scss">

    #home-frame {

        display: flex;
        flex-direction: column;
        min-height: 100vh;
        overflow: hidden;

        background: #224C74 radial-gradient(#336699, #1e385c);

        .app-header {
            padding: 1.5rem 6rem 0.5rem;
            color: rgba(255, 255, 255, 0.7);
            position: relative;
            z-index: 10;

            .header-top {
                display: flex;
                align-items: center;
            }


            .header-brand {
                display: flex;
                flex-direction: column;
            }

            h1 {
                font-weight: 400;
                font-size: 28px;
                margin-top: 0;
                cursor: pointer;
                white-space: nowrap;

                strong {
                    color: rgb(255, 255, 255);
                }
            }

            .header-version {
                font-size: 11px;
                color: rgba(255, 255, 255, 0.4);
                text-align: right;
                margin-top: -4px;
                line-height: 1;
            }

            .header-right {
                margin-left: auto;
                text-align: right;
            }

            .menu {
                color: rgb(255, 255, 255);
                font-weight: bold;

                i {
                    margin-left: 1rem;
                    cursor: pointer;

                    &:hover {
                        color: white;
                    }
                }

                a {
                    color: white;
                }
            }

            .mobile-logout {
                display: none;
            }

            @media (max-width: 768px) {
                padding: 0.75rem 1rem 0.5rem;

                .header-top {
                    flex-wrap: wrap;
                }

                h1 {
                    font-size: 18px;
                }

                .header-version {
                    display: none;
                }

                .mobile-logout {
                    display: block;
                    color: rgba(255, 255, 255, 0.7);
                    font-size: 1.1rem;
                    margin-left: auto;

                    &:hover {
                        color: white;
                    }
                }

                .header-right {
                    width: 100%;
                    text-align: left;
                    margin-left: 0;
                    margin-top: 0.25rem;
                }

                .hide-mobile {
                    display: none !important;
                }

                .menu {
                    font-size: 0.85rem;

                    .pb-3 {
                        padding-bottom: 0 !important;
                    }
                }
            }
        }

        main {
            position: relative;
            flex-grow: 1;

            .start {
                display: flex;
                justify-content: center;
                flex-direction: column;

                transition-delay: 0ms;
                transition-duration: 500ms;

                margin-top: 0.25rem;
                padding-left: 6rem;
                padding-right: 6rem;

                @media (max-width: 768px) {
                    padding-left: 1rem;
                    padding-right: 1rem;
                    margin-top: 1rem;
                }
            }

            // Favorites Section
            .favorites-menu {
                margin-bottom: 2rem;
                padding-bottom: 2rem;

                &.has-items {
                    border-bottom: 1px solid rgba(255, 255, 255, 0.15);
                }

                .items .menu-item .remove-favorite {
                    position: absolute;
                    top: -0.5rem;
                    left: -0.5rem;
                    background: #e74c3c;
                    color: white;
                    width: 22px;
                    height: 22px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.15s;
                    z-index: 1;

                    i {
                        font-size: 12px;
                    }

                    &:hover {
                        background: #c0392b;
                        transform: scale(1.1);
                    }
                }

                .favorites-empty {
                    color: rgba(255, 255, 255, 0.5);
                    font-size: 0.9rem;
                    padding: 0.5rem 0;

                    i {
                        color: #ffd700;
                        margin: 0 0.3rem;
                    }
                }
            }

            .welcome {
                color: white;
                padding: 0;
                margin-bottom: 3rem;

                h1 {
                    font-size: 2.5rem;
                    color: #ffffff;

                    strong {
                        color: #ffffff;
                    }
                }

                h3 {
                    font-size: 22px;
                    color: rgba(255, 255, 255, 0.8);
                }

                @media (max-width: 768px) {
                    padding: 0;
                    margin-bottom: 1.5rem;

                    h1 {
                        font-size: 1.3rem;
                    }

                    h3 {
                        font-size: 1rem;
                    }
                }
            }

            .menu-sections {
                padding-top: 3.5rem;
                border-top: 1px solid rgba(255, 255, 255, 0.15);

                .section-menu {
                    margin-top: 1rem;

                    h3 {
                        font-size: 0.85rem;
                        text-transform: uppercase;
                        letter-spacing: 0.05em;
                        margin-bottom: 0.75rem;
                    }
                }

                .section-menu-header {
                    font-size: 0.85rem;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    margin-bottom: 0.75rem;
                }

                .new-quote-btn {
                    background: transparent;
                    border: 1px solid rgba(255, 255, 255, 0.3);
                    color: white;
                    height: 100%;
                    border-radius: 2rem;
                    font-size: 0.85rem;
                    cursor: pointer;
                    transition: all linear 0.15s;

                    &:hover {
                        border-color: white;
                        color: white;
                        background: rgba(255, 255, 255, 0.05);
                    }
                }

                .items-compact {
                    .menu-item-sm {
                        width: 5.5rem;
                        padding: 1.2rem 0 0.7rem;
                        margin: 0 0.7rem 0.7rem 0;

                        i {
                            font-size: 1.6rem;
                        }

                        .menu-title {
                            font-size: 0.75rem;
                            padding-top: 0.3rem;
                        }

                        .menu-tag {
                            top: -0.4rem;
                            right: -0.4rem;
                            min-width: 22px;
                            font-size: 10px;
                            padding: 2px 0.3rem 1px;
                        }

                        .favorite-toggle {
                            width: 22px;
                            height: 22px;
                            top: -0.4rem;
                            left: -0.4rem;

                            i {
                                font-size: 12px;
                            }
                        }

                        @media (max-width: 768px) {
                            width: calc(50% - 0.5rem);
                            margin: 0 0.5rem 0.5rem 0;
                        }
                    }
                }

            }

            .favorites-bar {
                margin-top: 3rem;
                padding-top: 1rem;
                border-top: 1px solid rgba(255, 255, 255, 0.15);
                display: flex;
                align-items: center;

                a {
                    font-size: 0.85rem;
                    color: white;
                    cursor: pointer;

                    i {
                        color: #ffd700;
                        margin-right: 0.3rem;
                    }

                    &:hover {
                        color: white;
                    }
                }

                .version-label {
                    margin-left: auto;
                    font-size: 0.75rem;
                    color: white;

                    strong {
                        color: white;
                    }
                }
            }

            .menu {
                margin-top: 0;

                &.legacy-menu {
                    margin-top: 3rem;
                }

                .items {
                    display: flex;
                    flex-wrap: wrap;
                    /*justify-content: space-between;*/

                    .menu-item {
                        border: 1px solid rgba(255, 255, 255, 0.1);
                        color: white;
                        text-align: center;
                        padding: 1.8rem 0 1rem;
                        margin: 0 1.1rem 1rem 0;
                        border-radius: 1.1rem;
                        position: relative;
                        width: 7.5rem;
                        cursor: pointer;
                        transition: all linear 0.15s;

                        &:hover {
                            background: rgba(255, 255, 255, 0.05);
                            border: 1px solid rgba(255, 255, 255, 1);
                        }

                        i {
                            font-size: 2.4rem;
                        }

                        .menu-title {
                            display: block;
                            padding-top: 0.45rem;
                            font-size: 0.85rem;
                        }

                        .menu-tag {
                            position: absolute;
                            top: -0.5rem;
                            right: -0.5rem;
                            background-color: white;
                            color: #336699;
                            padding: 3px 0.4rem 1px;
                            min-width: 26px;
                            font-size: 12px;
                            font-weight: bold;
                            border-radius: 10px;

                            i {
                                font-size: 12px;
                                margin-right: 4px !important;
                                margin-left: 6px !important;

                                &:first-child {
                                    margin-left: 0 !important;
                                }
                            }
                        }

                        .favorite-toggle {
                            position: absolute;
                            top: -0.5rem;
                            left: -0.5rem;
                            background: rgba(0, 0, 0, 0.6);
                            color: #ffd700;
                            width: 26px;
                            height: 26px;
                            border-radius: 50%;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            cursor: pointer;
                            transition: all 0.15s;
                            z-index: 1;

                            i {
                                font-size: 14px;
                            }

                            &:hover {
                                background: rgba(0, 0, 0, 0.8);
                                transform: scale(1.1);
                            }
                        }

                        @media (max-width: 768px) {
                            width: calc(50% - 0.5rem);
                            margin: 0 0.5rem 0.5rem 0;
                            padding: 1.2rem 0 0.7rem;
                            border-radius: 0.8rem;

                            i {
                                font-size: 1.8rem;
                            }

                            .menu-title {
                                font-size: 0.75rem;
                                padding-top: 0.3rem;
                            }

                            .menu-tag {
                                top: -0.4rem;
                                right: -0.4rem;
                                min-width: 20px;
                                font-size: 10px;
                                padding: 2px 0.3rem 1px;
                            }

                            .favorite-toggle {
                                width: 22px;
                                height: 22px;
                                top: -0.4rem;
                                left: -0.4rem;

                                i {
                                    font-size: 11px;
                                }
                            }
                        }
                    }
                }

                @media (max-width: 768px) {
                    margin: 0;
                    padding: 0;
                }
            }

            .sub-menu {
                display: flex;
                flex-wrap: wrap;

                @media (max-width: 768px) {
                    margin-top: 1rem;
                }

                .menu-item {
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    color: white;
                    text-align: center;
                    padding: 0.3rem 1.1rem 0.2rem 1.1rem;
                    margin: 1rem 1rem 0 0;
                    border-radius: 1.1rem;
                    position: relative;
                    cursor: pointer;
                    transition: all linear 0.15s;

                    &:hover {
                        background: rgba(255, 255, 255, 0.05);
                        border: 1px solid rgba(255, 255, 255, 1);
                    }

                    i {
                        margin-right: 0.6rem;
                    }

                    .menu-title {
                        font-size: 0.85rem;
                    }

                    .tag {
                        background-color: white;
                        color: #336699;
                        padding: 0.23rem 0.55rem;
                        font-size: 11px;
                        font-weight: bold;
                        border-radius: 10px;
                        margin-left: 0.7rem;
                    }

                    @media (max-width: 768px) {
                        width: calc(50% - 0.5rem);
                        margin: 0.5rem 0.5rem 0 0;
                    }
                }
            }

            .start input {
                border: 1px solid rgba(255, 255, 255, 0.1);

                background: transparent;
                border-radius: 2rem;
                color: white;

                &:focus {
                    border: 1px solid rgba(255, 255, 255, 0.6);
                }
            }

            .start .icon {
                color: white !important;
            }

            // Admin Stats Widgets
            .admin-widgets {
                margin-top: 0;
                display: flex;
                flex-wrap: wrap;
                gap: 0.75rem;
                align-items: flex-start;

                .stat-widget {
                    background: transparent;
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    border-radius: 1.1rem;
                    flex: 1 1 10rem;
                    margin-bottom: 0;
                    padding: 0.8rem 1rem;
                    color: white;
                    transition: all linear 0.15s;

                    &:hover {
                        background: rgba(255, 255, 255, 0.05);
                        border-color: rgba(255, 255, 255, 1);
                    }

                    .widget-header {
                        display: flex;
                        align-items: center;
                        margin-bottom: 0.6rem;
                        padding-bottom: 0.6rem;
                        border-bottom: 1px solid rgba(255, 255, 255, 0.1);

                        i {
                            font-size: 1.5rem;
                            margin-right: 0.8rem;
                            color: rgba(255, 255, 255, 0.9);
                        }

                        .widget-title {
                            font-size: 1.1rem;
                            font-weight: 600;
                            color: rgba(255, 255, 255, 0.95);
                        }
                    }

                    .widget-body {
                        .stat-row {
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            padding: 0.4rem 0;

                            &:not(:last-child) {
                                border-bottom: 1px solid rgba(255, 255, 255, 0.05);
                            }

                            .stat-label {
                                font-size: 0.9rem;
                                color: rgba(255, 255, 255, 0.7);
                            }

                            .stat-value {
                                font-size: 1.4rem;
                                font-weight: 600;
                                color: white;
                                display: flex;
                                align-items: center;
                                gap: 0.8rem;

                                .trend {
                                    font-size: 0.75rem;
                                    padding: 0.2rem 0.5rem;
                                    border-radius: 0.5rem;
                                    font-weight: 600;
                                    display: flex;
                                    align-items: center;
                                    gap: 0.3rem;

                                    &.trend-up {
                                        background: rgba(72, 187, 120, 0.2);
                                        color: #68D391;

                                        i {
                                            color: #68D391;
                                        }
                                    }

                                    &.trend-down {
                                        background: rgba(252, 129, 129, 0.2);
                                        color: #FC8181;

                                        i {
                                            color: #FC8181;
                                        }
                                    }

                                    &.trend-neutral {
                                        background: rgba(255, 255, 255, 0.1);
                                        color: rgba(255, 255, 255, 0.6);

                                        i {
                                            color: rgba(255, 255, 255, 0.6);
                                        }
                                    }

                                    i {
                                        font-size: 0.7rem;
                                    }
                                }
                            }
                        }
                    }
                }


                @media (max-width: 1024px) {
                    margin-top: 2rem;
                }
            }
        }

        footer {
            color: white;
            padding: 2rem 3rem 1rem;

            .mh-breadcrumb {
                list-style: none;
                margin: 0;
                padding: 0;
                font-size: 0.75rem;

                transition-delay: 0ms;
                transition-duration: 500ms;
                opacity: 0;

                &.active {
                    opacity: 1;
                }

                li {
                    display: inline-block;
                    cursor: pointer;

                    i {
                        margin-right: 0.5rem;
                    }

                    span {
                        display: inline-block;
                        margin-bottom: 3px;
                        border-bottom: 1px solid transparent;
                    }

                    &.active span {
                        border-bottom: 1px solid rgba(255, 255, 255, 0.2);
                    }

                    &::after {
                        font-family: "Font Awesome 6 Pro", serif;
                        font-weight: 400;
                        content: "\f054";
                        color: rgba(255, 255, 255, 0.3);
                        font-size: 0.8rem;
                        margin: 0 0.7rem;
                    }

                    &:last-child::after {
                        content: ""
                    }
                }
            }

            @media (max-width: 768px) {
                display: none;
            }
        }
    }

</style>

<style lang="scss">
    #home-frame .warning {
        margin-top: 15px;
        display: inline-block;
        color: white;
        background-color: orange;
        border-radius: 16px;
        padding: 2px 12px 2px 10px;
        font-size: 12px;
        font-weight: bold;

        i {
            margin-right: 6px;
        }
    }

    #home-frame img {
        padding-bottom: 3px;
        padding-top: 3px;
    }

    #home-frame .start .control {
        ::placeholder {
            color: rgba(255, 255, 255, 0.22);
            opacity: 1; /* Firefox */
        }

        .icon {
            padding-left: 1rem;
        }

        .input {
            padding-left: 3rem !important;
        }
    }

    .recent-users-widget .widget-body {
        margin: 0 0.25rem;
    }

    .recent-users-widget .recent-user-row {
        padding: 0.2rem 0.4rem;
        cursor: pointer !important;
        transition: background 0.15s;
    }

    .recent-users-widget .recent-user-divider {
        margin: 0;
        border: none;
        height: 1px;
        background: rgba(255, 255, 255, 0.08);
    }

    .recent-users-widget .recent-user-row:hover {
        background: rgba(255, 255, 255, 0.08);
        border-radius: 0.4rem;
    }

    .recent-users-widget .recent-user-name {
        font-size: 0.82rem;
        font-weight: 500;
        color: rgba(255, 255, 255, 0.9);
    }
</style>
